import { ProductCard as ProductTile } from '../../components/ProductCard/index.js';
import { storefrontAccessToken, storefrontAccessTokenDev } from '../../config';

const isIntl = window.Shopify?.country !== 'US';

function extractStyleCode(src) {
  const filename = src.split('/').pop().split('?')[0];
  const match = filename.match(/^(\d{5}-\d{3,6})/);
  return match ? match[1] : '';
}

function extractPosition(src) {
  const filename = src.split('/').pop().split('?')[0];
  const pos = parseInt(filename.split('_')[1] || '', 10);
  return Number.isNaN(pos) ? Infinity : pos;
}

function buildStyleCodeMap(product) {
  const variantToStyleCode = new Map();

  (product.variants || []).forEach((variant) => {
    const src = variant.featured_image?.src;
    if (!src) return;
    const styleCode = extractStyleCode(src);
    if (styleCode) variantToStyleCode.set(variant.id.toString(), styleCode);
  });

  if (variantToStyleCode.size === 0) {
    const images = product.images || [];
    const firstSrc = typeof images[0] === 'string' ? images[0] : images[0]?.src;
    const fallback = firstSrc ? extractStyleCode(firstSrc) : '';
    if (fallback) {
      (product.variants || []).forEach((v) => variantToStyleCode.set(v.id.toString(), fallback));
    }
  }

  return variantToStyleCode;
}

function buildMediaArray(product) {
  const images = product.images || [];
  return images
    .map((img) => (typeof img === 'string' ? img : img.src))
    .filter(Boolean)
    .sort((a, b) => extractPosition(a) - extractPosition(b));
}

function mapVariants(variants, product, styleCodeMap, inventoryMap) {
  const currency_iso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';

  return variants.map((variant) => {
    const styleCode = styleCodeMap.get(variant.id.toString()) || variant.barcode || variant.sku || '';
    const variantId = variant.id.toString();

    let inventory;
    let inventoryQtyIntl;
    if (inventoryMap) {
      const qty = inventoryMap[variantId] ?? 0;
      inventory = qty > 0 ? 1 : 0;
      inventoryQtyIntl = qty;
    } else {
      inventory = variant.available ? 1 : 0;
      inventoryQtyIntl = variant.available ? 1 : 0;
    }

    return {
      data: {
        ...variant,
        Sellable: variant.inventory_quantity > 0 || variant.available,
        imgs: variant.featured_image?.src || product.featured_image,
        image_url: variant.featured_image?.src || product.featured_image,
        color: variant.option1 || '',
        size: variant.option2 || '',
        option1: variant.option1 || '',
        option2: variant.option2 || '',
        variation_id: variantId,
        variant_position: variant.position || 0,
        inventory,
        inventory_quantity_international: inventoryQtyIntl,
        'Product-sku': variant.sku || '',
        barcode: styleCode,
        price: `${currency_iso}:${(variant.price / 100).toFixed(2)}`,
        compare_at_price: variant.compare_at_price / 100,
      },
    };
  });
}

function isVariantAvailable(mappedVariant) {
  return isIntl
    ? Number(mappedVariant.data.inventory_quantity_international) > 0
    : Number(mappedVariant.data.inventory) > 0;
}

function getAvailableOptions(mappedVariants) {
  if (!mappedVariants || !mappedVariants.length) {
    return [];
  }

  const colorOptions = new Map();

  const createColorOption = (mv) => ({
    color: mv.data.color,
    barcode: mv.data.barcode || '',
    variantId: mv.data.variation_id,
    img: mv.data.image_url,
  });

  const processVariants = (filterCondition) => {
    mappedVariants.forEach((mv) => {
      if (!filterCondition(mv)) return;

      const color = mv.data.color;
      if (!colorOptions.has(color)) {
        colorOptions.set(color, createColorOption(mv));
      }
    });
  };

  if (mappedVariants.some(isVariantAvailable)) {
    processVariants(isVariantAvailable);
  } else {
    processVariants(() => true);
  }

  return Array.from(colorOptions.values());
}

async function fetchPublishedProducts(products) {
  const handles = products.map((p) => p.handle).filter(Boolean);
  if (handles.length === 0) return new Set();

  const token = window.Shopify?.shop === 'windsor-us.myshopify.com' ? storefrontAccessToken : storefrontAccessTokenDev;
  const country = window.Shopify?.country || 'US';

  const productFields = handles.map((handle, i) => `p${i}: product(handle: "${handle}") { id }`).join('\n');
  const query = `query @inContext(country: ${country}) { ${productFields} }`;

  try {
    const response = await fetch(`https://${window.Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    const data = await response.json();
    if (!data?.data) return new Set();

    const publishedProductIds = new Set();
    Object.values(data.data).forEach((product) => {
      if (!product?.id) return;
      publishedProductIds.add(product.id.split('/').pop());
    });
    return publishedProductIds;
  } catch (error) {
    console.error('[ProductTiles] Storefront API error:', error);
    return new Set();
  }
}

async function fetchIntlInventory(products) {
  const skuToVariantId = {};
  products.forEach((product) => {
    (product.variants || []).forEach((variant) => {
      if (variant.sku) skuToVariantId[variant.sku] = variant.id.toString();
    });
  });

  const allSkus = Object.keys(skuToVariantId);
  if (allSkus.length === 0) return {};

  const url =
    window.Shopify?.shop === 'windsor-dev.myshopify.com'
      ? 'https://inventory-lookup-service.wndsr.dev/international_availability?token=s9bf4mDcowLlyL5PIb7KHVa0'
      : 'https://inventory-lookup-service.wndsr.io/international_availability?token=s9bf4mDcowLlyL5PIb7KHVa0';

  const BATCH_SIZE = 100;
  const chunks = [];
  for (let i = 0; i < allSkus.length; i += BATCH_SIZE) {
    chunks.push(allSkus.slice(i, i + BATCH_SIZE));
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const responses = await Promise.all(
      chunks.map((chunk) =>
        fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ skus: chunk.join(',') }),
          signal: controller.signal,
        }).then((r) => r.json()),
      ),
    );
    clearTimeout(timeoutId);

    const inventoryMap = {};
    responses.forEach((data) => {
      Object.entries(data).forEach(([sku, qty]) => {
        const variantId = skuToVariantId[sku];
        if (variantId) inventoryMap[variantId] = qty || 0;
      });
    });
    return inventoryMap;
  } catch (error) {
    console.error('[ProductTiles] Inventory service error:', error);
    return null;
  }
}

async function fetchIntlAvailability(products) {
  const [publishedProductIds, inventoryMap] = await Promise.all([
    fetchPublishedProducts(products),
    fetchIntlInventory(products),
  ]);
  return { publishedProductIds, inventoryMap };
}

// Wire up impression tracking on tiles via IntersectionObserver
function setupImpressionObserver(tileImpressionData) {
  const impressionObserver = new IntersectionObserver(
    (entries) => {
      const visibleProducts = [];
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const data = tileImpressionData.get(entry.target);
          if (data) {
            visibleProducts.push(data);
            tileImpressionData.delete(entry.target);
            impressionObserver.unobserve(entry.target);
          }
        }
      });
      if (visibleProducts.length > 0) {
        const dataLayer = window.dataLayer || [];
        dataLayer.push({
          event: 'impressions',
          ecommerce: {
            impressions: { products: visibleProducts },
          },
        });
      }
      if (tileImpressionData.size === 0) {
        impressionObserver.disconnect();
      }
    },
    { root: null, rootMargin: '0px', threshold: 0.1 },
  );

  tileImpressionData.forEach((_, tileEl) => impressionObserver.observe(tileEl));
}

function renderProducts(
  flattenedData,
  container,
  { productLimit, sectionTitle, sectionId, promoCreative, location, inventoryMap, publishedProductIds },
) {
  let availableProductsCount = 0;
  const tileImpressionData = new Map();

  flattenedData.forEach((product, productIndex) => {
    if (!product || !product.variants || product.variants.length === 0) return;
    if (availableProductsCount >= productLimit) return;
    if (publishedProductIds && !publishedProductIds.has(product.id.toString())) return;

    const styleCodeMap = buildStyleCodeMap(product);
    const mappedVariants = mapVariants(product.variants, product, styleCodeMap, inventoryMap);

    const firstAvailable = mappedVariants.find(isVariantAvailable);
    if (!firstAvailable) return;

    availableProductsCount++;

    const availableVariants = mappedVariants.filter(isVariantAvailable);

    const availableOptions = getAvailableOptions(availableVariants);
    const media = buildMediaArray(product);

    const rawVariant = product.variants.find((v) => v.id.toString() === firstAvailable.data.variation_id);
    const rawPrice = rawVariant?.price || 0;
    const rawComparePrice = rawVariant?.compare_at_price || 0;
    const maxComparePrice = product.compare_at_price_max || product.compare_at_price_min || rawComparePrice;

    const productTileElement = document.createElement('div');
    productTileElement.classList.add('product-card-c-basis');
    productTileElement.setAttribute('data-product-id', product.id);

    const productTileData = {
      pid: product.id.toString(),
      productId: product.id.toString(),
      title: product.title,
      productUrl: `/products/${product.handle}`,
      productType: product.type || '',
      imageUrl: firstAvailable.data.image_url || product.featured_image,
      imageAlt: product.title,
      media,
      price: rawPrice / 100,
      compareAtPrice: rawComparePrice / 100,
      maxCompareAtPrice: maxComparePrice / 100,
      tags: product.tags || [],
      variants: mappedVariants,
      availableVariants,
      options: availableOptions,
      isRec: true,
      shouldReportClickToAdmind: false,
      selectedColor: firstAvailable.data.color || '',
      recToken: null,
      index: productIndex,
      reviews: null,
      handle: product.handle,
      promoCreative: sectionTitle,
      trackingId: `${sectionId}-${productIndex}`,
      sectionId,
      location,
      productCardNode: productTileElement,
      variantId: firstAvailable.data.variation_id,
      colorSwatches: true,
      quickAdd: true,
      isBadgeVisible: true,
    };

    try {
      new ProductTile(productTileData);

      const firstStyleCode = firstAvailable.data.barcode || '';
      tileImpressionData.set(productTileElement, {
        id: product.id.toString(),
        style: firstStyleCode,
        list: promoCreative,
        subcategory: product.type || '',
        name: product.title.replace(/((CLEARANCE)|(PLACEHOLDER))( - )/, ''),
        price: (rawPrice / 100).toFixed(2),
        originalPrice: rawComparePrice > 0 ? (rawComparePrice / 100).toFixed(2) : (rawPrice / 100).toFixed(2),
        newArrival: (product.tags || []).includes('New Arrival'),
        isMarkedDown: rawComparePrice > 0 && rawPrice < rawComparePrice,
        hasReviews: false,
        reviewCount: 0,
        reviewAverage: 0,
        showsQuadpay: !!document.querySelector('[data-klarna-visible]'),
        almostGone: false,
        position: availableProductsCount,
      });

      container.appendChild(productTileElement);
    } catch (error) {
      console.error('Error creating ProductTile for:', product.title, error);
    }
  });

  setTimeout(() => setupImpressionObserver(tileImpressionData), 0);
}

function postRenderSwiperUpdate(swiperEl) {
  if (!swiperEl?.swiper) return;

  const wrapper = swiperEl.querySelector('.swiper-wrapper');
  if (wrapper) {
    Array.from(wrapper.children)
      .filter((child) => !child.classList.contains('swiper-slide'))
      .forEach((child) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        wrapper.insertBefore(slide, child);
        slide.appendChild(child);
      });
  }
  swiperEl.swiper.update();

  const imgEl = swiperEl.querySelector('.product-rec-c-carousel__product-card-images, .product-card-c-basis__images');
  if (imgEl) {
    const setArrowPos = () => {
      if (imgEl.offsetHeight > 0) {
        swiperEl.parentNode.style.setProperty('--product-image-height', `${Math.round(imgEl.offsetHeight / 2)}px`);
      }
    };
    setArrowPos();
    if (imgEl.offsetHeight === 0) {
      const img = imgEl.querySelector('img');
      if (img) img.addEventListener('load', setArrowPos, { once: true });
    }
  }

  const navContainer = swiperEl.nextElementSibling;
  if (navContainer) {
    const swiper = swiperEl.swiper;
    navContainer.querySelectorAll('.swiper-prev').forEach((btn) => {
      btn.classList.toggle('swiper-button-disabled', swiper.isBeginning);
      btn.toggleAttribute('disabled', swiper.isBeginning);
    });
    navContainer.querySelectorAll('.swiper-next').forEach((btn) => {
      btn.classList.toggle('swiper-button-disabled', swiper.isEnd);
      btn.toggleAttribute('disabled', swiper.isEnd);
    });
  }
}

// Main export function
export default () => {
  const elements = document.querySelectorAll('[data-product-tiles]');

  elements.forEach((el) => {
    try {
      const container = el.parentNode;

      const jsonElement = container.querySelector('script[data-product-tile-data]');
      if (!jsonElement || !jsonElement.textContent) {
        console.error('No product data found for element:', el);
        return;
      }

      const productData = JSON.parse(jsonElement.textContent);
      if (!productData || !Array.isArray(productData)) {
        console.error('Invalid product data format for element:', el);
        return;
      }

      const flattenedData = productData.flat().filter(Boolean);
      const limit = el.getAttribute('data-product-limit');

      const sectionEl = el.closest('section, footer');
      const sectionTitle = sectionEl?.querySelector('p')?.textContent;
      const promoFrame = el.closest('[data-promo-creative], [data-promo-title]');
      const sectionId = sectionEl?.getAttribute('data-section-id') || el.id || 'product-carousel';
      const promoCreative = promoFrame?.getAttribute('data-promo-creative') || 'Product Carousel';
      const promoTitle = promoFrame?.getAttribute('data-promo-title') || sectionTitle;
      // Read before the container is emptied below, which detaches `el`
      const tilesLocation = el.getAttribute('data-location') || '';

      const swiperEl = container.closest('[swiper]');
      if (isIntl && swiperEl) {
        container.style.minHeight = `${swiperEl.offsetHeight || 400}px`;
      }
      container.innerHTML = '';

      container.setAttribute('data-promo-tracking', '');
      container.setAttribute('data-section-id', sectionId);
      container.setAttribute('data-tracking-id', sectionId);
      container.setAttribute('data-promo-creative', promoCreative);
      container.setAttribute('data-promo-title', promoTitle);

      const productLimit = limit ? parseInt(limit) : Infinity;
      const renderCtx = {
        productLimit,
        sectionTitle,
        sectionId,
        promoCreative,
        location: tilesLocation,
        inventoryMap: null,
        publishedProductIds: null,
      };

      if (isIntl) {
        fetchIntlAvailability(flattenedData).then(({ inventoryMap, publishedProductIds }) => {
          renderCtx.inventoryMap = inventoryMap;
          renderCtx.publishedProductIds = publishedProductIds;
          renderProducts(flattenedData, container, renderCtx);
          container.style.minHeight = '';
          el.setAttribute('initialized', 'true');
          postRenderSwiperUpdate(swiperEl, container);
        });
      } else {
        renderProducts(flattenedData, container, renderCtx);
        el.setAttribute('initialized', 'true');
      }
    } catch (error) {
      console.error('Error rendering product tiles:', error);
    }
  });
};
